/**
 * Raketech Compare Page
 * 3-way comparison: Raketech reconciliation vs adwstats synced data vs AC data
 * Period: 2023-03 to 2024-11
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Scale,
  Download,
  Upload,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
  Filter,
  Search,
  Database,
  FileText,
  RefreshCw,
  Link2,
  Unlink,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ComparisonIssue {
  type: string;
  severity: string;
  accountId: string;
  username: string;
  brand: string;
  partner: string;
  offer: string;
  geo: string;
  currency: string;
  details: string;
  partnerGeoKey: string;

  invoiceFTDs?: number;
  invoiceRevenue?: number;
  reconciliationFTDs?: number;
  reconciliationEarningsOriginal?: number;
  reconciliationEarnings?: number;
  reconciliationRows?: number;
  partnerFTDs?: number;
  partnerCommissionOriginal?: number;
  partnerCommission?: number;
  partnerCommissionOurShare?: number;
  partnerSource?: string;

  adwstatsCommission?: number;
  adwstatsCommissionOriginal?: number;
  adwstatsCurrency?: string;
  adwstatsAccountId?: string;
  adwstatsAccountName?: string;
  adwstatsFTDs?: number;
  adwstatsMatchMethod?: string;
  adwstatsDataSynced?: boolean;

  manualCheckAmount?: number;
  manualCheckComment?: string;
  manualCheckSource?: string;

  ftdDifference?: number;
  revenueDifference?: number;
  percentageDifference?: number;
  exchangeRate?: number;

  missingInInvoice?: boolean;
  missingInReconciliation?: boolean;
  missingInPartner?: boolean;

  invoiceBrandKey?: string;
}

interface ComparisonGroup {
  partner: string;
  geo: string;
  issues: ComparisonIssue[];
  totalInvoiced: number;
  shouldBe: number;
  underpaid: number;
  totalSynced: number;
}

interface CompareData {
  period: string;
  exchangeRates: Record<string, number>;
  summary: {
    totalAccounts: number;
    matched: number;
    critical: number;
    underpaid: number;
    overpaid: number;
    missingInInvoice: number;
    missingInRecon: number;
    missingInPartner: number;
    noAccountsTracked: number;
  };
  totals: {
    invoiced: number;
    actualInvoiced: number;
    actualInvoiceCount: number;
    shouldBe: number;
    reconEarnings: number;
    partnerOurShare: number;
    adwstatsSynced: number;
    manualCheck: number;
    positiveCorrections: number;
    negativeCorrections: number;
  };
  adwstatsMatching: {
    matched: number;
    unmatched: number;
    matchedNotSynced: number;
    unmatchedAccounts: { accountId: string; username: string; partner: string; geo: string }[];
    totalLocalAccounts: number;
  };
  groups: ComparisonGroup[];
  issues: ComparisonIssue[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getAuthToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) {
      const parsed = JSON.parse(auth);
      return parsed.state?.token || null;
    }
  } catch { /* ignore */ }
  return null;
};

const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);

const fmtOriginalCurrency = (v: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${currency} ${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
};

const fmtNum = (v: number) => v.toLocaleString();

type TabFilter = 'all' | 'critical' | 'underpaid' | 'overpaid' | 'missing_invoice' | 'missing_recon' | 'missing_partner' | 'matches';

const SEVERITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const RaketechCompare: React.FC = () => {
  const [isImporting, setIsImporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [compareData, setCompareData] = useState<CompareData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // -----------------------------------------------------------------------
  // Import data from Google Sheets
  // -----------------------------------------------------------------------
  const handleImport = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { toast.error('Not authenticated'); return; }

    setIsImporting(true);
    try {
      // Phased import — each phase is a separate request with its own time
      // budget so the whole job can't exceed a single Worker request limit.
      const phases = ['invoices', 'reconciliation', 'partner', 'extras'] as const;
      const summary: Record<string, any> = {};
      for (const phase of phases) {
        toast.info(`Importing: ${phase}…`);
        const resp = await fetch(`/api/raketech/import?phase=${phase}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await resp.json() as any;
        if (!json.success) throw new Error(`${phase}: ${json.error || json.message || 'Import failed'}`);
        Object.assign(summary, json.data.summary);
      }
      toast.success(`Import complete: ${summary.reconciliation || 0} recon, ${summary.invoices || 0} invoices, ${summary.partner_accounts || 0} partner accounts`);
      fetchComparison();
    } catch (err: any) {
      toast.error(err.message || 'Import failed');
    } finally {
      setIsImporting(false);
    }
  }, []);

  // -----------------------------------------------------------------------
  // Fetch comparison data
  // -----------------------------------------------------------------------
  const fetchComparison = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { toast.error('Not authenticated'); return; }

    setIsLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/raketech/compare', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await resp.json() as any;
      if (!json.success) throw new Error(json.error || json.message || 'Comparison failed');
      if (json.data.empty) {
        setError(json.data.message);
        setCompareData(null);
      } else {
        setCompareData(json.data);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchComparison(); }, [fetchComparison]);

  // -----------------------------------------------------------------------
  // Filter issues
  // -----------------------------------------------------------------------
  const filteredIssues = useMemo(() => {
    if (!compareData) return [];
    let items = compareData.issues;

    switch (activeTab) {
      case 'critical': items = items.filter(i => i.severity === 'critical'); break;
      case 'underpaid': items = items.filter(i => i.type === 'underpayment'); break;
      case 'overpaid': items = items.filter(i => (i.revenueDifference || 0) > 1); break;
      case 'missing_invoice': items = items.filter(i => i.missingInInvoice && !i.missingInReconciliation); break;
      case 'missing_recon': items = items.filter(i => i.missingInReconciliation); break;
      case 'missing_partner': items = items.filter(i => i.missingInPartner && !i.missingInReconciliation); break;
      case 'matches': items = items.filter(i => i.type === 'match'); break;
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(i =>
        i.accountId?.toLowerCase().includes(q) ||
        i.username?.toLowerCase().includes(q) ||
        i.brand?.toLowerCase().includes(q) ||
        i.partner?.toLowerCase().includes(q) ||
        i.offer?.toLowerCase().includes(q) ||
        i.geo?.toLowerCase().includes(q) ||
        i.details?.toLowerCase().includes(q) ||
        i.adwstatsAccountName?.toLowerCase().includes(q)
      );
    }

    return items.sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9));
  }, [compareData, activeTab, searchTerm]);

  // Group filtered issues by partner+geo
  const filteredGroups = useMemo(() => {
    const groups = new Map<string, ComparisonGroup>();
    for (const iss of filteredIssues) {
      const key = iss.partnerGeoKey;
      if (!groups.has(key)) {
        groups.set(key, { partner: iss.partner, geo: iss.geo, issues: [], totalInvoiced: 0, shouldBe: 0, underpaid: 0, totalSynced: 0 });
      }
      const g = groups.get(key)!;
      g.issues.push(iss);
      g.totalInvoiced += iss.invoiceRevenue || 0;
      const syncedComm = (iss.adwstatsDataSynced !== false && iss.adwstatsCommission) ? iss.adwstatsCommission : 0;
      g.shouldBe += Math.max(iss.reconciliationEarnings || 0, iss.partnerCommissionOurShare || 0, syncedComm, iss.manualCheckAmount || 0);
      if ((iss.revenueDifference || 0) < -1) g.underpaid += Math.abs(iss.revenueDifference || 0);
      if (iss.adwstatsAccountId && iss.adwstatsDataSynced !== false) g.totalSynced += iss.adwstatsCommission || 0;
    }
    return Array.from(groups.values()).sort((a, b) => b.underpaid - a.underpaid);
  }, [filteredIssues]);

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <Scale className="h-7 w-7 text-purple-400" />
              Raketech Reconciliation
            </h1>
            <p className="text-zinc-400 mt-1 text-sm sm:text-base">
              3-way comparison: Raketech recon vs Adwstats synced vs invoices (Mar 2023 – Nov 2024)
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleImport}
              disabled={isImporting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors disabled:opacity-50"
            >
              {isImporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              {isImporting ? 'Importing...' : 'Import from Sheets'}
            </button>
            <button
              onClick={fetchComparison}
              disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Loading */}
      {isLoading && !compareData && (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-zinc-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading comparison data...</span>
          </div>
        </div>
      )}

      {/* Error / empty state */}
      {error && !isLoading && (
        <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">No Data Available</h2>
          <p className="text-sm text-zinc-400 mb-6">{error}</p>
          <button
            onClick={handleImport}
            disabled={isImporting}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-500 hover:bg-purple-600 disabled:bg-purple-500/50 text-white font-medium text-sm rounded-lg transition-colors"
          >
            {isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {isImporting ? 'Importing...' : 'Import from Google Sheets'}
          </button>
        </div>
      )}

      {/* Main content */}
      {compareData && (
        <div className="space-y-6 animate-fade-in">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {compareData.totals.actualInvoiced > 0 && (
              <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-white/10 p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <FileText className="h-3.5 w-3.5 text-white" />
                  <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Actual Invoiced</p>
                </div>
                <p className="text-lg font-bold text-white tabular-nums">{fmtCurrency(compareData.totals.actualInvoiced)}</p>
                <p className="text-[10px] text-zinc-600">{compareData.totals.actualInvoiceCount} invoices</p>
              </div>
            )}
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <FileText className="h-3.5 w-3.5 text-zinc-400" />
                <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Commission Sheets</p>
              </div>
              <p className="text-lg font-bold text-zinc-300 tabular-nums">{fmtCurrency(compareData.totals.invoiced)}</p>
              {compareData.totals.actualInvoiced > 0 && (
                <p className={`text-[10px] tabular-nums ${compareData.totals.invoiced < compareData.totals.actualInvoiced ? 'text-red-400' : 'text-zinc-600'}`}>
                  {compareData.totals.invoiced < compareData.totals.actualInvoiced
                    ? `Gap: ${fmtCurrency(compareData.totals.actualInvoiced - compareData.totals.invoiced)}`
                    : 'Matches invoices'}
                </p>
              )}
            </div>
            <SummaryCard label="Reconciliation" value={fmtCurrency(compareData.totals.shouldBe)} icon={<Scale className="h-4 w-4" />} color="purple" />
            <SummaryCard label="Recon Earnings" value={fmtCurrency(compareData.totals.reconEarnings)} icon={<ArrowUpRight className="h-4 w-4" />} color="amber" />
            <SummaryCard label="Partner 95%" value={fmtCurrency(compareData.totals.partnerOurShare)} icon={<ArrowDownRight className="h-4 w-4" />} color="blue" />
            <SummaryCard label="Our Synced (AC)" value={fmtCurrency(compareData.totals.adwstatsSynced)} icon={<Database className="h-4 w-4" />} color="emerald" />
            {compareData.totals.manualCheck > 0 && (
              <SummaryCard label="Manual Check" value={fmtCurrency(compareData.totals.manualCheck)} icon={<CheckCircle2 className="h-4 w-4" />} color="cyan" />
            )}
          </div>

          {/* Corrections & Matching */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-emerald-500/20 p-4">
              <p className="text-xs text-zinc-400 mb-1">Corrections (+)</p>
              <p className="text-lg font-bold text-emerald-400 tabular-nums">
                +{fmtCurrency(compareData.totals.positiveCorrections)}
              </p>
            </div>
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-red-500/20 p-4">
              <p className="text-xs text-zinc-400 mb-1">Corrections (-)</p>
              <p className="text-lg font-bold text-red-400 tabular-nums">
                {fmtCurrency(compareData.totals.negativeCorrections)}
              </p>
            </div>
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
              <p className="text-xs text-zinc-400 mb-1">Adwstats Matching <span className="text-zinc-600">({compareData.adwstatsMatching.totalLocalAccounts} accounts)</span></p>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1 text-sm text-emerald-400">
                  <Link2 className="h-3.5 w-3.5" />
                  {compareData.adwstatsMatching.matched} matched
                </span>
                <span className="flex items-center gap-1 text-sm text-amber-400">
                  <Unlink className="h-3.5 w-3.5" />
                  {compareData.adwstatsMatching.unmatched} unmatched
                </span>
                {compareData.adwstatsMatching.matchedNotSynced > 0 && (
                  <span className="flex items-center gap-1 text-sm text-amber-400/70">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {compareData.adwstatsMatching.matchedNotSynced} not synced
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Tabs + Search */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
              <Filter className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
              {([
                ['all', `All (${compareData.summary.totalAccounts})`],
                ['critical', `Critical (${compareData.summary.critical})`],
                ['underpaid', `Underpaid (${compareData.summary.underpaid})`],
                ['overpaid', `Overpaid (${compareData.summary.overpaid})`],
                ['missing_invoice', `No Invoice (${compareData.summary.missingInInvoice})`],
                ['missing_recon', `No Recon (${compareData.summary.missingInRecon})`],
                ['missing_partner', `No Partner (${compareData.summary.missingInPartner})`],
                ['matches', `Matches (${compareData.summary.matched})`],
              ] as [TabFilter, string][]).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`px-2.5 py-1 text-xs rounded-md whitespace-nowrap transition-colors ${
                    activeTab === key
                      ? 'bg-purple-500/10 text-purple-400 border border-purple-500/30'
                      : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex-1" />
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search accounts..."
                className="pl-8 pr-3 py-1.5 w-56 text-xs bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-purple-500/50"
              />
            </div>
          </div>

          {/* Grouped Comparison Table */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
            <div className="p-4 border-b border-[hsl(225,15%,18%)]">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Scale className="h-4 w-4 text-purple-400" />
                Comparison Results ({filteredIssues.length} accounts in {filteredGroups.length} groups)
              </h3>
            </div>

            {filteredGroups.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm">No results match your filters</div>
            ) : (
              <div className="divide-y divide-[hsl(225,15%,15%)]">
                {filteredGroups.map(group => {
                  const groupKey = `${group.partner}|${group.geo}`;
                  const isExpanded = expandedGroups.has(groupKey);
                  const underpaidPct = group.shouldBe > 0 ? ((group.totalInvoiced - group.shouldBe) / group.shouldBe * 100) : 0;

                  return (
                    <div key={groupKey}>
                      {/* Group header */}
                      <button
                        onClick={() => toggleGroup(groupKey)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[hsl(225,15%,12%)] transition-colors text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {isExpanded ? <ChevronDown className="h-4 w-4 text-zinc-500 shrink-0" /> : <ChevronRight className="h-4 w-4 text-zinc-500 shrink-0" />}
                          <span className="text-sm font-medium text-white truncate">{group.partner || 'Unknown'}</span>
                          <span className="text-xs text-zinc-500">-</span>
                          <span className="text-xs text-purple-400 font-medium">{group.geo}</span>
                          <span className="text-[10px] text-zinc-600">{group.issues.length} accounts</span>
                        </div>
                        <div className="flex items-center gap-4 shrink-0">
                          <div className="text-right">
                            <span className="text-[10px] text-zinc-500 block">Invoiced</span>
                            <span className="text-xs text-white tabular-nums">{fmtCurrency(group.totalInvoiced)}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] text-zinc-500 block">Reconciliation</span>
                            <span className="text-xs text-amber-400 tabular-nums">{fmtCurrency(group.shouldBe)}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] text-zinc-500 block">Our Synced</span>
                            <span className="text-xs text-emerald-400 tabular-nums">{fmtCurrency(group.totalSynced)}</span>
                          </div>
                          {group.underpaid > 0 && (
                            <div className="text-right">
                              <span className="text-[10px] text-zinc-500 block">Underpaid</span>
                              <span className="text-xs text-red-400 tabular-nums">
                                {fmtCurrency(group.underpaid)}
                                {underpaidPct !== 0 && <span className="text-[10px] ml-1">({underpaidPct.toFixed(1)}%)</span>}
                              </span>
                            </div>
                          )}
                        </div>
                      </button>

                      {/* Group rows */}
                      {isExpanded && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-[hsl(225,15%,8%)]">
                              <tr>
                                <th className="text-left px-3 py-2 text-zinc-500 font-medium">Severity</th>
                                <th className="text-left px-3 py-2 text-zinc-500 font-medium">Account ID</th>
                                <th className="text-left px-3 py-2 text-zinc-500 font-medium">Username</th>
                                <th className="text-left px-3 py-2 text-zinc-500 font-medium">Offer</th>
                                <th className="text-right px-3 py-2 text-zinc-500 font-medium">Invoice (EUR)</th>
                                <th className="text-right px-3 py-2 text-zinc-500 font-medium">Reconciliation</th>
                                <th className="text-right px-3 py-2 text-zinc-500 font-medium">Partner 95%</th>
                                <th className="text-right px-3 py-2 text-zinc-500 font-medium">Our Synced</th>
                                <th className="text-right px-3 py-2 text-zinc-500 font-medium">Manual Check</th>
                                <th className="text-right px-3 py-2 text-zinc-500 font-medium">Diff</th>
                                <th className="text-left px-3 py-2 text-zinc-500 font-medium">Account</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                              {group.issues.map((iss, idx) => {
                                const diff = iss.revenueDifference || 0;
                                const diffColor = Math.abs(diff) < 1 ? 'text-zinc-600' : diff < 0 ? 'text-red-400' : 'text-emerald-400';

                                return (
                                  <tr key={`${iss.accountId}-${idx}`} className="hover:bg-[hsl(225,15%,12%)]">
                                    <td className="px-3 py-2">
                                      <SeverityBadge severity={iss.severity} />
                                    </td>
                                    <td className="px-3 py-2 text-zinc-300 tabular-nums">{iss.accountId || '—'}</td>
                                    <td className="px-3 py-2 text-white font-medium">{iss.username || (iss.type === 'no_accounts_tracked' && iss.brand ? <span className="text-amber-400/80 text-[10px]">{iss.brand}</span> : '—')}</td>
                                    <td className="px-3 py-2 text-zinc-400 max-w-[140px] truncate">{iss.offer || '—'}</td>
                                    <td className="px-3 py-2 text-right text-zinc-300 tabular-nums">
                                      {iss.invoiceRevenue !== undefined ? fmtCurrency(iss.invoiceRevenue) : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right text-amber-400 tabular-nums">
                                      {iss.reconciliationEarnings !== undefined ? fmtCurrency(iss.reconciliationEarnings) : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right text-blue-400 tabular-nums">
                                      {iss.partnerCommissionOurShare !== undefined ? fmtCurrency(iss.partnerCommissionOurShare) : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                      {iss.adwstatsAccountId ? (
                                        iss.adwstatsDataSynced === false ? (
                                          <span className="text-amber-400/70 text-[10px]">not synced</span>
                                        ) : (
                                          <span className="text-emerald-400">
                                            {fmtCurrency(iss.adwstatsCommission || 0)}
                                            {iss.adwstatsCurrency && iss.adwstatsCurrency !== 'EUR' && iss.adwstatsCommissionOriginal != null && (
                                              <span className="block text-[9px] text-emerald-400/50">
                                                {fmtOriginalCurrency(iss.adwstatsCommissionOriginal, iss.adwstatsCurrency)}
                                              </span>
                                            )}
                                          </span>
                                        )
                                      ) : <span className="text-zinc-600">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                      {iss.manualCheckAmount ? (
                                        <span className="text-cyan-400" title={iss.manualCheckComment || iss.manualCheckSource || ''}>
                                          {fmtCurrency(iss.manualCheckAmount)}
                                          {iss.manualCheckSource && (
                                            <span className="block text-[9px] text-cyan-400/60">{iss.manualCheckSource}</span>
                                          )}
                                        </span>
                                      ) : <span className="text-zinc-600">—</span>}
                                    </td>
                                    <td className={`px-3 py-2 text-right tabular-nums font-medium ${diffColor}`}>
                                      {iss.revenueDifference !== undefined ? fmtCurrency(diff) : '—'}
                                    </td>
                                    <td className="px-3 py-2">
                                      {iss.adwstatsAccountName ? (
                                        <span className="flex items-center gap-1">
                                          <Link2 className={`h-3 w-3 ${iss.adwstatsDataSynced === false ? 'text-amber-500' : 'text-emerald-500'}`} />
                                          <span className="text-zinc-300 truncate max-w-[120px]" title={iss.adwstatsAccountName}>
                                            {iss.adwstatsAccountName}
                                          </span>
                                          {iss.adwstatsDataSynced === false ? (
                                            <span className="text-[9px] text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1 rounded">not synced</span>
                                          ) : (
                                            <span className="text-[9px] text-zinc-600">({iss.adwstatsMatchMethod})</span>
                                          )}
                                        </span>
                                      ) : iss.accountId ? (
                                        <span className="text-zinc-600 flex items-center gap-1">
                                          <Unlink className="h-3 w-3" />
                                          <span className="text-[10px]">unmatched</span>
                                        </span>
                                      ) : '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Unmatched Adwstats Accounts */}
          {compareData.adwstatsMatching.unmatched > 0 && (
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-amber-500/20 overflow-hidden">
              <div className="p-4 border-b border-[hsl(225,15%,15%)]">
                <h3 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
                  <Unlink className="h-4 w-4" />
                  Unmatched Raketech Accounts ({compareData.adwstatsMatching.unmatched})
                </h3>
                <p className="text-[10px] text-zinc-500 mt-1">
                  These Raketech accounts have no matching adwstats account. Use the mapping API to manually link them.
                </p>
              </div>
              <div className="overflow-x-auto max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[hsl(225,15%,12%)]">
                    <tr>
                      <th className="text-left px-3 py-2 text-zinc-500 font-medium">Account ID</th>
                      <th className="text-left px-3 py-2 text-zinc-500 font-medium">Username</th>
                      <th className="text-left px-3 py-2 text-zinc-500 font-medium">Partner</th>
                      <th className="text-left px-3 py-2 text-zinc-500 font-medium">GEO</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(225,15%,15%)]">
                    {compareData.adwstatsMatching.unmatchedAccounts.map((acc, idx) => (
                      <tr key={idx} className="hover:bg-[hsl(225,15%,12%)]">
                        <td className="px-3 py-2 text-zinc-300 tabular-nums">{acc.accountId}</td>
                        <td className="px-3 py-2 text-white">{acc.username}</td>
                        <td className="px-3 py-2 text-zinc-400">{acc.partner}</td>
                        <td className="px-3 py-2 text-purple-400">{acc.geo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Exchange Rates Info */}
          <div className="bg-[hsl(225,15%,8%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
            <p className="text-xs text-zinc-500">
              Exchange rates used: {Object.entries(compareData.exchangeRates).map(([cur, rate]) =>
                `1 ${cur} = ${Number(rate).toFixed(4)} EUR`
              ).join(', ')} (averages from Google Sheet)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const SummaryCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = ({ label, value, icon, color }) => {
  const colorMap: Record<string, string> = {
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
    purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
    cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
    zinc: 'text-zinc-300 bg-[hsl(225,15%,12%)] border-[hsl(225,15%,18%)]',
  };
  const c = colorMap[color] || colorMap.zinc;
  const parts = c.split(' ');
  return (
    <div className={`rounded-xl border p-3 ${parts.slice(1).join(' ')}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={parts[0]}>{icon}</span>
        <span className="text-[10px] text-zinc-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-base sm:text-lg font-bold tabular-nums ${parts[0]}`}>{value}</p>
    </div>
  );
};

const SeverityBadge: React.FC<{ severity: string }> = ({ severity }) => {
  const config: Record<string, { label: string; cls: string }> = {
    critical: { label: 'Critical', cls: 'bg-red-500/10 text-red-400 border-red-500/20' },
    high: { label: 'High', cls: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
    medium: { label: 'Medium', cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
    low: { label: 'Low', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
    info: { label: 'Info', cls: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' },
  };
  const c = config[severity] || config.info;
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium ${c.cls}`}>
      {c.label}
    </span>
  );
};

export default RaketechCompare;
